'use client';

/**
 * Microsoft Clarity loader — HIPAA-conservative safe-list + consent gate.
 *
 * Two gates, both must pass:
 *   1. Path is on the explicitly allow-listed marketing routes. PHI or
 *      auth-sensitive routes (/dashboard, /checkout, /login,
 *      /forgot-password, /reset-password, /api/*) never load Clarity.
 *   2. The user has accepted analytics cookies via CookieConsentBanner
 *      (`cookie_consent` JSON cookie, `analytics === true`).
 *
 * Known limitation: @microsoft/clarity has no "stop" — once Clarity loads
 * in a browser tab on a safe page, same-tab client-side navigations to a
 * PHI page will still be observed by Clarity's internal router hooks.
 * The firm guarantee is that PHI-first visits (direct links to /dashboard
 * etc.) never request clarity.ms. The defense-in-depth fix is a CSP that
 * blocks clarity.ms on PHI routes — tracked for a later phase.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Clarity from '@microsoft/clarity';
import { COOKIE_NAME as CONSENT_COOKIE } from '@/components/CookieConsentBanner';

const SAFE_LIST: RegExp[] = [
  /^\/$/,
  /^\/flow-\d+$/,
  /^\/home$/,
  /^\/about$/,
  /^\/faq$/,
  /^\/privacy-policy$/,
  /^\/refund-policy$/,
  /^\/terms$/,
  /^\/support$/,
  /^\/get-started$/,
  /^\/success$/,
  /^\/weight-loss$/,
];

function isSafeRoute(pathname: string): boolean {
  return SAFE_LIST.some((re) => re.test(pathname));
}

function analyticsConsented(): boolean {
  if (typeof document === 'undefined') return false;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`));
  if (!match) return false;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]!));
    return !!(parsed && parsed.analytics === true);
  } catch {
    return false;
  }
}

export default function MicrosoftClarity() {
  const pathname = usePathname();
  const initialized = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (initialized.current) return;
    if (!isSafeRoute(pathname)) return;
    if (!analyticsConsented()) return;
    Clarity.init('v9c57qk7jb');
    initialized.current = true;
  }, [pathname]);

  return null;
}
